import { useEffect, useState } from "react"
import { Circle, Minus, Moon, Plus, Sun } from "lucide-react"
import { toast } from "sonner"
import { WallPresetIcon } from "@/components/locations/create-wall-preset-icons"
import { PlatformSectorCountInput } from "@/components/locations/platform-sector-count-input"
import { SearchableCombobox } from "@/components/ui/searchable-combobox"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { Button, Input } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { openingCameraPathTake } from "@/lib/location-scene-3d/camera-path-takes"
import { captureAndUploadScene3dPoster } from "@/lib/location-scene-3d/capture-scene-poster"
import { createScene3d, type LibraryScene3dItem } from "@/lib/library-api"
import {
  DEFAULT_WALL_HEIGHT,
  DEFAULT_WALL_LAYOUT_PRESET,
  emptyPlatform,
  HEIGHT_MAX,
  sectorSizeMeters,
  seedPerimeterWallCells,
  WALL_LAYOUT_PRESETS,
  wallSidesForPreset,
  type PlatformUnit,
  type ScenePlatform,
  type WallLayoutPreset,
} from "@/lib/location-scene-3d/platform"
import { sectorsFromDraft } from "@/lib/location-scene-3d/platform-size-draft"
import { defaultPointLightIntensity } from "@/lib/location-scene-3d/light-marker"
import { locationListThumbnail, type LocationThumbnailSource } from "@/lib/location-thumbnail"
import {
  defaultPrevizLayout,
  defaultSceneEnvironment,
  defaultSceneLight,
  sceneStateForSave,
  type SceneSkyMarkup,
  type SceneThemeId,
} from "@/lib/location-scene-3d/scene-state"

export type Scene3dLocationOption = LocationThumbnailSource & {
  id: number
  name: string
}

type LocationScene3dCreateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  /** Fixed location when creating from a location card; null when chooser may pick. */
  locationId: number | null
  locationName: string
  /** When set (and locationId is null), show optional location picker. */
  locationOptions?: Scene3dLocationOption[]
  onCreated: (scene3d: LibraryScene3dItem) => void
  frameFormat?: string | null
  nested?: boolean | 2 | 3
}

const NONE_LOCATION_VALUE = ""

function clampWallHeight(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WALL_HEIGHT
  return Math.min(HEIGHT_MAX, Math.max(1, Math.round(value)))
}

/** Digits-only draft; empty allowed while typing (avoids Number("") → 0 / leading zeros). */
function digitsDraftFromRaw(raw: string, maxDigits: number): string {
  return raw.replace(/\D/g, "").slice(0, maxDigits)
}

function wallHeightDraftFromRaw(raw: string): string {
  return digitsDraftFromRaw(raw, String(HEIGHT_MAX).length)
}

function wallHeightFromDraft(draft: string): number {
  if (draft === "") return DEFAULT_WALL_HEIGHT
  return clampWallHeight(Number(draft))
}

export function LocationScene3dCreateDialog({
  open,
  onOpenChange,
  projectId,
  locationId,
  locationName,
  locationOptions,
  onCreated,
  frameFormat = null,
  nested,
}: LocationScene3dCreateDialogProps) {
  const { t } = useTranslation(["workspace"])
  const empty = emptyPlatform()
  const locationLocked = locationId != null
  const showLocationPicker = !locationLocked && (locationOptions?.length ?? 0) > 0
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(locationId)
  const [sectorsXDraft, setSectorsXDraft] = useState(() => String(empty.sectors_x))
  const [sectorsZDraft, setSectorsZDraft] = useState(() => String(empty.sectors_z))
  const [wallHeightDraft, setWallHeightDraft] = useState(() => String(DEFAULT_WALL_HEIGHT))
  const [unit, setUnit] = useState<PlatformUnit>(() => empty.unit)
  const [theme, setTheme] = useState<SceneThemeId>("light")
  const [skyMarkup, setSkyMarkup] = useState<SceneSkyMarkup>("plus")
  const [wallPreset, setWallPreset] = useState<WallLayoutPreset>(DEFAULT_WALL_LAYOUT_PRESET)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedLocationId(locationId)
  }, [open, locationId])

  const resetForm = () => {
    const next = emptyPlatform()
    setSectorsXDraft(String(next.sectors_x))
    setSectorsZDraft(String(next.sectors_z))
    setWallHeightDraft(String(DEFAULT_WALL_HEIGHT))
    setUnit(next.unit)
    setTheme("light")
    setSkyMarkup("plus")
    setWallPreset(DEFAULT_WALL_LAYOUT_PRESET)
    setSelectedLocationId(locationId)
    setBusy(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm()
    onOpenChange(next)
  }

  const commitSectorsX = () => {
    const next = sectorsFromDraft(sectorsXDraft)
    setSectorsXDraft(String(next))
  }

  const commitSectorsZ = () => {
    const next = sectorsFromDraft(sectorsZDraft)
    setSectorsZDraft(String(next))
  }

  const commitWallHeight = () => {
    setWallHeightDraft(String(wallHeightFromDraft(wallHeightDraft)))
  }

  const handleCreate = async () => {
    if (busy) return
    setBusy(true)
    try {
      const nextPlatform: ScenePlatform = {
        sectors_x: sectorsFromDraft(sectorsXDraft),
        sectors_z: sectorsFromDraft(sectorsZDraft),
        sector_size: empty.sector_size,
        unit,
      }
      const wallHeight = wallHeightFromDraft(wallHeightDraft)
      const cells = seedPerimeterWallCells(nextPlatform, wallSidesForPreset(wallPreset), wallHeight)
      const span = Math.max(nextPlatform.sectors_x, nextPlatform.sectors_z) * sectorSizeMeters(nextPlatform)
      const opening = openingCameraPathTake(t("locationsPage.scene3d.timeline.takeName", { n: 1 }))
      const layout = {
        ...defaultPrevizLayout(),
        camera_path: opening.path,
        camera_paths: [opening],
        active_camera_path_id: opening.id,
        platform: nextPlatform,
        cells,
        environment: { ...defaultSceneEnvironment(), theme, sky_markup: skyMarkup },
        exists: true,
        light: {
          ...defaultSceneLight(span),
          intensity: defaultPointLightIntensity(span),
        },
      }
      const effectiveLocationId = locationLocked ? locationId : selectedLocationId
      const pickedName =
        effectiveLocationId != null
          ? (locationOptions?.find((loc) => loc.id === effectiveLocationId)?.name ?? locationName)
          : locationName
      const sceneState = sceneStateForSave(layout)
      const created = await createScene3d(projectId, {
        name: pickedName,
        locationId: effectiveLocationId,
        sceneState,
      })
      await captureAndUploadScene3dPoster({
        projectId,
        scene3dId: created.id,
        sceneState: layout,
        frameFormat,
      })
      resetForm()
      onOpenChange(false)
      onCreated(created)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t("locationsPage.scene3d.createDialog.title")}
      closeAriaLabel={t("locationsPage.scene3d.close")}
      size="md"
      nested={nested}
      footer={
        <>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => handleOpenChange(false)}>
            {t("locationsPage.scene3d.createDialog.cancel")}
          </Button>
          <Button type="button" disabled={busy} onClick={() => void handleCreate()}>
            {busy ? t("locationsPage.scene3d.createDialog.creating") : t("locationsPage.scene3d.createDialog.create")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-3 p-4">
        {showLocationPicker ? (
          <>
            <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.createDialog.location")}</span>
            <SearchableCombobox
              className="min-w-0"
              disabled={busy}
              portalToBody
              ariaLabel={t("locationsPage.scene3d.createDialog.location")}
              placeholder={t("locationsPage.scene3d.createDialog.noLocation")}
              value={selectedLocationId != null ? String(selectedLocationId) : NONE_LOCATION_VALUE}
              onChange={(value) => {
                setSelectedLocationId(value === NONE_LOCATION_VALUE ? null : Number(value))
              }}
              options={[
                { value: NONE_LOCATION_VALUE, label: t("locationsPage.scene3d.createDialog.noLocation") },
                ...(locationOptions ?? []).map((loc) => ({
                  value: String(loc.id),
                  label: loc.name,
                  thumbUrl: locationListThumbnail(loc),
                })),
              ]}
            />
          </>
        ) : null}

        <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.createDialog.platform")}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <PlatformSectorCountInput
            value={sectorsXDraft}
            disabled={busy}
            title={t("locationsPage.scenePlatform.sectorsX")}
            aria-label={t("locationsPage.scenePlatform.sectorsX")}
            onValueChange={setSectorsXDraft}
            onCommit={commitSectorsX}
            className="h-8 w-16"
          />
          <span className="text-sm text-ui-text-muted" aria-hidden>
            ×
          </span>
          <PlatformSectorCountInput
            value={sectorsZDraft}
            disabled={busy}
            title={t("locationsPage.scenePlatform.sectorsZ")}
            aria-label={t("locationsPage.scenePlatform.sectorsZ")}
            onValueChange={setSectorsZDraft}
            onCommit={commitSectorsZ}
            className="h-8 w-16"
          />
          <SegmentedControl
            disabled={busy}
            value={unit}
            onChange={(id) => setUnit(id as PlatformUnit)}
            items={[
              { id: "m", label: t("locationsPage.scenePlatform.unitM") },
              { id: "cm", label: t("locationsPage.scenePlatform.unitCm") },
            ]}
          />
        </div>

        <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.createDialog.walls")}</span>
        <SegmentedControl
          className="w-fit justify-self-start"
          disabled={busy}
          value={wallPreset}
          onChange={(id) => setWallPreset(id as WallLayoutPreset)}
          items={WALL_LAYOUT_PRESETS.map((preset) => {
            const label = t(`locationsPage.scene3d.createDialog.wallPreset.${preset}`)
            return {
              id: preset,
              icon: <WallPresetIcon preset={preset} />,
              ariaLabel: label,
              hint: label,
            }
          })}
        />

        <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.createDialog.wallHeight")}</span>
        <Input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={wallHeightDraft}
          disabled={busy}
          title={t("locationsPage.scene3d.createDialog.wallHeight")}
          aria-label={t("locationsPage.scene3d.createDialog.wallHeight")}
          onChange={(event) => setWallHeightDraft(wallHeightDraftFromRaw(event.target.value))}
          onBlur={commitWallHeight}
          className="h-8 w-14 justify-self-start"
        />

        <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.environment.theme")}</span>
        <SegmentedControl
          className="w-fit justify-self-start"
          disabled={busy}
          value={theme}
          onChange={(id) => setTheme(id as SceneThemeId)}
          items={[
            {
              id: "light",
              label: t("locationsPage.scene3d.environment.themeLight"),
              icon: <Sun className="h-3.5 w-3.5" aria-hidden />,
            },
            {
              id: "dark",
              label: t("locationsPage.scene3d.environment.themeDark"),
              icon: <Moon className="h-3.5 w-3.5" aria-hidden />,
            },
          ]}
        />

        <span className="text-sm text-ui-text-secondary">{t("locationsPage.scene3d.environment.skyMarkup")}</span>
        <SegmentedControl
          className="w-fit justify-self-start"
          disabled={busy}
          value={skyMarkup}
          onChange={(id) => setSkyMarkup(id as SceneSkyMarkup)}
          items={[
            {
              id: "plus",
              label: t("locationsPage.scene3d.environment.markupPlus"),
              icon: <Plus className="h-3.5 w-3.5" aria-hidden />,
            },
            {
              id: "dots",
              label: t("locationsPage.scene3d.environment.markupDots"),
              icon: <Circle className="h-1.5 w-1.5 fill-current" aria-hidden />,
            },
            {
              id: "none",
              label: t("locationsPage.scene3d.environment.markupNone"),
              icon: <Minus className="h-3.5 w-3.5" aria-hidden />,
            },
          ]}
        />
      </div>
    </WorkspaceDialog>
  )
}
